import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Alert, Spin, Row, Col, Table, Tag, Space, Empty, Button,
  Modal, Input, message, Tooltip as AntTooltip,
} from 'antd';
import {
  EyeOutlined, SendOutlined, ExclamationCircleOutlined, ArrowUpOutlined,
  ArrowLeftOutlined, LinkOutlined, InteractionOutlined, RedoOutlined,
} from '@ant-design/icons';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Text } = Typography;

/* ── Status colour tokens ── */
const STATUS_PIE_COLORS = {
  sent:    'var(--text-3)',
  opened:  '#059669',
  clicked: '#0a40d9',
  bounced: 'var(--danger)',
};

/* ── Stat card ── */
const MetricCard = ({ icon, label, value, color, bg, suffix = '' }) => (
  <Card
    style={{
      borderRadius: 'var(--r-lg)',
      border: '1px solid var(--border)',
      boxShadow: 'var(--shadow-sm)',
      background: 'var(--surface)',
      height: '100%',            // equal height across all cards in the row
    }}
    bodyStyle={{ padding: 16 }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {React.cloneElement(icon, { style: { fontSize: 20, color } })}
      </div>
      {/* minWidth:0 lets the text truncate instead of growing the card */}
      <div style={{ minWidth: 0, flex: 1 }}>
        <Text style={{
          color: 'var(--text-3)', fontSize: 12.5,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block',
        }}>{label}</Text>
        <div style={{
          fontSize: 'clamp(18px, 2vw, 24px)', fontWeight: 800, color: 'var(--text)',
          lineHeight: 1.15, fontFamily: 'var(--font-mono)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {value}<span style={{ fontSize: 13, color, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{suffix}</span>
        </div>
      </div>
    </div>
  </Card>
);

const Reports = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [emails,   setEmails]   = useState([]);
  const [stats,    setStats]    = useState(null);
  const [clicks,   setClicks]   = useState({ urlSummary: [], events: [] });
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [nonOpeners, setNonOpeners] = useState(null); // { non_openers, completed }
  const [resendModal, setResendModal] = useState(false);
  const [resendSubject, setResendSubject] = useState('');
  const [resending, setResending] = useState(false);
  const token  = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [c, e, s, cl] = await Promise.all([
          axios.get(`/api/campaigns/${id}`,        { headers }),
          axios.get(`/api/campaigns/${id}/emails`,  { headers }),
          axios.get(`/api/campaigns/${id}/stats`,   { headers }),
          axios.get(`/api/campaigns/${id}/clicks`,  { headers }),
        ]);
        setCampaign(c.data);
        setEmails(e.data);
        setStats(s.data);
        setClicks(cl.data);
        // Resend eligibility (don't block the report if this fails)
        try {
          const no = await axios.get(`/api/campaigns/${id}/non-openers-count`, { headers });
          setNonOpeners(no.data);
        } catch { /* ignore */ }
      } catch {
        setError('Failed to load campaign report');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const openResend = () => {
    setResendSubject(campaign?.subject ? `${campaign.subject}`.startsWith('Re: ') ? campaign.subject : `Re: ${campaign.subject}` : '');
    setResendModal(true);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await axios.post(`/api/campaigns/${id}/resend-non-openers`, { subject: resendSubject }, { headers });
      message.success(`Resending to ${res.data.recipients} non-opener${res.data.recipients === 1 ? '' : 's'} 🚀`);
      setResendModal(false);
      navigate(`/campaigns/${res.data.campaign_id}/report`);
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  const pieData = stats ? [
    { name: 'Sent',    value: stats.sent    },
    { name: 'Opened',  value: stats.opened  },
    { name: 'Clicked', value: stats.clicked },
    { name: 'Bounced', value: stats.bounced },
  ].filter(d => d.value > 0) : [];

  /* ── Table columns ── */
  const columns = [
    {
      title: 'Subscriber', key: 'sub',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{r.Subscriber?.name || '—'}</div>
          <div style={{ color: 'var(--text-3)', fontSize: 12 }}>{r.Subscriber?.email || `ID: ${r.subscriber_id}`}</div>
        </div>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 110,
      render: s => {
        const toneMap = { sent: null, opened: 'info', clicked: 'ok', bounced: 'danger' };
        const tone = toneMap[s];
        const pill = {
          display: 'inline-block',
          padding: '2px 10px',
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 500,
          background: tone ? `var(--${tone}-bg)` : 'var(--surface-2)',
          color:      tone ? `var(--${tone})`    : 'var(--text-2)',
          border:     tone ? `1px solid var(--${tone})` : '1px solid var(--border)',
        };
        return <span style={pill}>{s.charAt(0).toUpperCase() + s.slice(1)}</span>;
      },
    },
    {
      title: 'Sent At', dataIndex: 'sent_at', width: 160,
      render: d => d ? <Text style={{ fontSize: 12, color: 'var(--text-2)' }}>{new Date(d).toLocaleString()}</Text> : '—',
    },
    {
      title: 'Opened', dataIndex: 'opened_at', width: 160,
      render: d => d
        ? <Text style={{ fontSize: 12, color: '#059669', fontFamily: 'var(--font-mono)' }}>{new Date(d).toLocaleString()}</Text>
        : <span style={{ color: 'var(--text-3)' }}>—</span>,
    },
    {
      title: 'Clicked', dataIndex: 'clicked_at', width: 160,
      render: d => d
        ? <Text style={{ fontSize: 12, color: '#0a40d9', fontFamily: 'var(--font-mono)' }}>{new Date(d).toLocaleString()}</Text>
        : <span style={{ color: 'var(--text-3)' }}>—</span>,
    },
    {
      title: 'Bounced', dataIndex: 'bounced_at', width: 160,
      render: d => d
        ? <Text style={{ fontSize: 12, color: 'var(--danger)', fontFamily: 'var(--font-mono)' }}>{new Date(d).toLocaleString()}</Text>
        : <span style={{ color: 'var(--text-3)' }}>—</span>,
    },
  ];

  const cardStyle = {
    borderRadius: 'var(--r-lg)',
    border: '1px solid var(--border)',
    boxShadow: 'var(--shadow-sm)',
    background: 'var(--surface)',
  };

  return (
    <div>
      {/* ── Back + campaign name ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/campaigns')}
          style={{ borderRadius: 'var(--r-md)', marginTop: 2 }}
        >
          Campaigns
        </Button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--text)', lineHeight: 1.2 }}>
            {campaign ? campaign.name : 'Campaign Report'}
          </div>
          <div style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 2 }}>
            {campaign ? `Subject: ${campaign.subject}` : 'Loading…'}
          </div>
        </div>

        {/* Resend to non-openers — only for completed campaigns with non-openers */}
        {nonOpeners?.completed && nonOpeners.non_openers > 0 && (
          <AntTooltip title={`${nonOpeners.non_openers} recipient${nonOpeners.non_openers === 1 ? '' : 's'} didn't open this campaign`}>
            <Button
              type="primary" icon={<RedoOutlined />} onClick={openResend}
              style={{ borderRadius: 'var(--r-md)', marginTop: 2 }}
            >
              Resend to non-openers ({nonOpeners.non_openers})
            </Button>
          </AntTooltip>
        )}
      </div>

      {/* Resend modal */}
      <Modal
        title={<Space><RedoOutlined />Resend to non-openers</Space>}
        open={resendModal}
        onCancel={() => setResendModal(false)}
        confirmLoading={resending}
        onOk={handleResend}
        okText={`Send to ${nonOpeners?.non_openers || 0} non-openers`}
        width={460}
      >
        <Text style={{ color: 'var(--text-2)', display: 'block', marginBottom: 16 }}>
          This creates a new campaign that sends the same email only to the{' '}
          <strong>{nonOpeners?.non_openers || 0}</strong> people who didn't open the original
          (unsubscribed/bounced addresses are excluded automatically).
        </Text>
        <Text type="secondary" style={{ fontSize: 13 }}>Subject line for the resend</Text>
        <Input
          value={resendSubject}
          onChange={(e) => setResendSubject(e.target.value)}
          placeholder="Subject line"
          style={{ marginTop: 4 }}
        />
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
          Tip: a fresh subject line often lifts opens on the second send.
        </Text>
      </Modal>

      {error && (
        <Alert
          message={error} type="error" showIcon closable onClose={() => setError('')}
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>
      ) : stats ? (
        <>
          {/* ── Metric cards ── */}
          <Row gutter={[16, 16]} align="stretch" style={{ marginBottom: 20 }}>
            <Col xs={12} sm={8} lg={4}>
              <MetricCard icon={<SendOutlined />}  label="Delivered"   value={stats.total}            color="#0a40d9" bg="var(--brand-tint)" />
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <MetricCard icon={<EyeOutlined />}   label="Open Rate"   value={stats.openRate}   suffix="%" color="#059669" bg="var(--ok-bg)" />
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <MetricCard icon={<ArrowUpOutlined />} label="Click Rate" value={stats.clickRate}  suffix="%" color="#0a40d9" bg="var(--brand-tint)" />
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <MetricCard icon={<InteractionOutlined />} label="Total Clicks" value={stats.totalClicks ?? 0} color="#0a40d9" bg="var(--brand-tint)" />
            </Col>
            <Col xs={12} sm={8} lg={4}>
              <MetricCard icon={<ExclamationCircleOutlined />} label="Bounce Rate" value={stats.bounceRate} suffix="%" color="var(--danger)" bg="var(--danger-bg)" />
            </Col>
          </Row>

          {/* ── Charts row ── */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            {pieData.length > 0 && (
              <Col xs={24} lg={10}>
                <Card style={cardStyle}>
                  <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 16, color: 'var(--text)' }}>
                    Status Breakdown
                  </Text>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={pieData} cx="50%" cy="50%"
                        innerRadius={60} outerRadius={100}
                        paddingAngle={3} dataKey="value"
                      >
                        {pieData.map(entry => (
                          <Cell key={entry.name} fill={STATUS_PIE_COLORS[entry.name.toLowerCase()] || 'var(--text-3)'} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, n) => [v, n]}
                        contentStyle={{ borderRadius: 'var(--r-md)', border: '1px solid var(--border)', background: 'var(--surface)' }}
                      />
                      <Legend iconType="circle" iconSize={10} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            )}

            <Col xs={24} lg={pieData.length > 0 ? 14 : 24}>
              <Card style={{ ...cardStyle, height: '100%' }}>
                <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 16, color: 'var(--text)' }}>
                  Delivery Summary
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { label: 'Total Emails',        count: stats.total,   color: 'var(--text)',    pct: '100%' },
                    { label: 'Sent (pending open)',  count: stats.sent,    color: 'var(--text-3)',  pct: stats.total > 0 ? ((stats.sent / stats.total) * 100).toFixed(1) + '%' : '—' },
                    { label: 'Opened',               count: stats.opened,  color: '#059669',        pct: stats.openRate + '%' },
                    { label: 'Clicked',              count: stats.clicked, color: '#0a40d9',        pct: stats.clickRate + '%' },
                    { label: 'Bounced',              count: stats.bounced, color: 'var(--danger)',  pct: stats.bounceRate + '%' },
                  ].map(({ label, count, color, pct }) => (
                    <div key={label} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '9px 14px',
                      background: 'var(--surface-2)',
                      borderRadius: 'var(--r-md)',
                    }}>
                      <Space size={8}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
                        <Text style={{ color: 'var(--text)', fontSize: 13 }}>{label}</Text>
                      </Space>
                      <Space size={16}>
                        <Text strong style={{ color, fontFamily: 'var(--font-mono)' }}>{count}</Text>
                        <Text style={{ color: 'var(--text-3)', fontSize: 12, minWidth: 40, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{pct}</Text>
                      </Space>
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>

          {/* ── Click URL breakdown ── */}
          {clicks.urlSummary.length > 0 && (
            <Card style={{ ...cardStyle, marginBottom: 20 }}>
              <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 16, color: 'var(--text)' }}>
                <LinkOutlined style={{ color: '#0a40d9', marginRight: 8 }} />
                Link Click Breakdown
              </Text>
              <Table
                dataSource={clicks.urlSummary}
                rowKey="url"
                size="small"
                pagination={false}
                scroll={{ x: 500 }}
                columns={[
                  {
                    title: 'URL', dataIndex: 'url', ellipsis: true,
                    render: url => (
                      <a
                        href={url} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#0a40d9', fontSize: 12 }}
                        onClick={e => e.stopPropagation()}
                      >
                        {url}
                      </a>
                    ),
                  },
                  {
                    title: 'Total Clicks', dataIndex: 'totalClicks', width: 120, align: 'center',
                    render: v => (
                      <span style={{ fontWeight: 700, color: '#0a40d9', fontSize: 14, fontFamily: 'var(--font-mono)' }}>{v}</span>
                    ),
                  },
                  {
                    title: 'Unique Clickers', dataIndex: 'uniqueSubscribers', width: 140, align: 'center',
                    render: v => (
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                        background: 'var(--brand-tint)', color: '#0a40d9',
                        border: '1px solid #0a40d933', fontSize: 12, fontWeight: 500,
                      }}>
                        {v} subscriber{v !== 1 ? 's' : ''}
                      </span>
                    ),
                  },
                ]}
              />
            </Card>
          )}

          {/* ── Individual records ── */}
          <Card style={cardStyle}>
            <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 16, color: 'var(--text)' }}>
              Individual Records
            </Text>
            <Table
              dataSource={emails}
              columns={columns}
              rowKey="id"
              size="small"
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No records yet" /> }}
              pagination={{ pageSize: 15, showTotal: t => `${t} record${t !== 1 ? 's' : ''}` }}
              scroll={{ x: 800 }}
            />
          </Card>
        </>
      ) : (
        <Card style={{ ...cardStyle, textAlign: 'center', padding: '40px 0' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text style={{ color: 'var(--text-3)' }}>No data available for this campaign yet</Text>}
          />
        </Card>
      )}
    </div>
  );
};

export default Reports;
